import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import TablePagination from "@material-ui/core/TablePagination";
import { useSnackbar } from "notistack";

import {
  FiltersPayloads,
  PageableReponse,
  PayloadWithPageAndFilter,
  SortPayload,
} from "@/types/ApiResponse";
import StaticTable, { DataStructure } from "@/components/StaticTable";
import TablePaginationActions from "@/components/ApiTablePagine/TablePaginationActions";
import { useSearchParams } from "react-router-dom";

interface ApiTablePagineProps<T> {
  getData: (
    filters: FiltersPayloads,
    sort: SortPayload,
    currentPage: number,
    size: number,
  ) => Promise<AxiosResponse<PageableReponse<T>>>;
  dataStructure: DataStructure<T>[];
  dataId: keyof T;
  showExportBtn?: boolean;
}

const DEFAULT_ELEMENTS_PER_PAGE = 10;
const ELMTS_PAR_PAGE_LIST = [5, 10, 25, 50];

export function ApiTablePagine<T>({
  dataStructure,
  getData,
  dataId,
}: ApiTablePagineProps<T>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const lastRequestId = useRef(0);
  const pushParamsToUrl = useRef(false);

  const [responsePageable, setReponsePageable] =
    useState<PageableReponse<T> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<FiltersPayloads>([]);
  const [sort, setSort] = useState<SortPayload | null>(null);
  const [page, setPage] = useState<number>(0);
  const [elementsParPage, setElementsParPage] = useState<number>(
    DEFAULT_ELEMENTS_PER_PAGE,
  );
  const [pageParams, setPageParams] = useSearchParams();
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const pageParam = pageParams.get("page");

    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10);
      if (!isNaN(pageNumber)) setPage(pageNumber);
    }

    const elementsParPageParam = pageParams.get("elementsParPage");

    if (elementsParPageParam) {
      const elementsParPageNumber = parseInt(elementsParPageParam, 10);

      if (
        !isNaN(elementsParPageNumber) &&
        ELMTS_PAR_PAGE_LIST.includes(elementsParPageNumber)
      ) {
        setElementsParPage(elementsParPageNumber);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pushParamsToUrl.current) return;

    if (page) pageParams.set("page", String(page));
    else pageParams.delete("page");

    if (elementsParPage)
      pageParams.set("elementsParPage", String(elementsParPage));
    else pageParams.delete("elementsParPage");

    setPageParams(pageParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, elementsParPage]);

  const fetchData = useCallback(
    async (payload: PayloadWithPageAndFilter) => {
      // Annuler le timeout précédent
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }

      // Créer un nouveau timeout
      debounceTimeout.current = setTimeout(async () => {
        const currentRequestId = ++lastRequestId.current;
        try {
          setLoading(true);
          const response = (await getData(
            //@ts-ignore
            getTransformedFilters(payload.filtres),
            payload.tri,
            payload.page,
            payload.size,
          )) as unknown as PageableReponse<T>;
          if (currentRequestId === lastRequestId.current) {
            setReponsePageable(response);
          }
        } catch (err) {
          if (currentRequestId === lastRequestId.current) {
            enqueueSnackbar("Une erreur interne est survenue", {
              variant: "error",
            });
          }
        } finally {
          if (currentRequestId === lastRequestId.current) setLoading(false);
        }
      }, 200);
    },
    [getData, enqueueSnackbar],
  );

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchData({
      filtres: filters,
      tri: sort as SortPayload,
      page: page,
      size: elementsParPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, page, elementsParPage]);

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    pushParamsToUrl.current = true;
    const newSize = parseInt(event.target.value, 10);

    if (Number.isNaN(newSize)) return;
    setPage(0);
    setElementsParPage(newSize);
  };

  return (
    <>
      <StaticTable
        data={responsePageable?.content || []}
        dataStructure={dataStructure}
        identifyRow={(row) => row[dataId]}
        filters={filters}
        setFilters={setFilters}
        sort={sort as SortPayload}
        onSort={setSort}
        loading={loading}
        elementsParPage={elementsParPage}
        renderFooter={() =>
          responsePageable ? (
            <TablePagination
              rowsPerPageOptions={ELMTS_PAR_PAGE_LIST}
              count={responsePageable.totalElements}
              rowsPerPage={elementsParPage}
              page={page}
              onPageChange={(event, newPage) => {
                pushParamsToUrl.current = true;
                setPage(newPage);
              }}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage={t("common.dmri.paginate.labelRowsPerPage")}
              ActionsComponent={TablePaginationActions}
            />
          ) : (
            <></>
          )
        }
      />
    </>
  );
}

/** Dispositif pour rendre l'objet d'entrée vers l'api 'plus sécurisé' */
const getTransformedFilters = (filtres: FiltersPayloads) =>
  filtres.reduce(
    (acc, { key, value, valueMin, valueMax, operateur }) => {
      acc[key] = { value, valueMin, valueMax, operateur };
      return acc;
    },
    {} as Record<
      string,
      {
        operateur: string;
        value?: string | number | Date;
        valueMin?: number | Date;
        valueMax?: number | Date;
      }
    >,
  );
